/** Smaller shared pieces: manager approval, barcodes, date ranges, charts, scanning. */
import { useEffect, useMemo, useRef, useState } from 'react';
import JsBarcode from 'jsbarcode';
import type { Permission } from '@pos/shared';
import { useI18n, useT } from '../i18n/index.js';
import { useAuth, type Approval } from '../state/auth.js';
import { useSettings } from '../state/settings.js';
import { Button, Field, Modal } from './ui.js';
import { presetRange, type RangeKey } from '../format.js';
import { dayKey } from '@pos/shared';
import { cameraAvailable, startCameraScan, type CameraScanner } from '../scanner.js';
import { beepOk, beepUnknown } from '../sound.js';
import { thumbOf } from '../image.js';

// ---------------------------------------------------------------------------
// Terms and privacy, under every sign-in
// ---------------------------------------------------------------------------

export function LegalLinks() {
  const t = useT();
  const { lang } = useI18n();
  const q = lang === 'ar' ? '?lang=ar' : '?lang=en';
  return (
    <nav className="legal-links" aria-label={t('legal.label')}>
      <a href={`https://appbay.dev/terms/${q}`} target="_blank" rel="noopener">{t('legal.terms')}</a>
      <span aria-hidden>·</span>
      <a href={`https://appbay.dev/privacy/${q}`} target="_blank" rel="noopener">{t('legal.privacy')}</a>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// A product photo
// ---------------------------------------------------------------------------

/**
 * Shows nothing at all when there is no photo, or when it cannot be loaded -
 * offline and never seen on this device - so a missing picture is never a
 * broken-image icon, just a tile without one.
 */
export function Photo({ url, small, className, alt = '' }: { url?: string | null; small?: boolean; className?: string; alt?: string }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [url]);
  if (!url || broken) return null;
  return (
    <img
      className={className}
      src={small ? thumbOf(url) : url}
      alt={alt}
      loading="lazy"
      decoding="async"
      draggable={false}
      onError={() => setBroken(true)}
    />
  );
}

// ---------------------------------------------------------------------------
// Manager approval, asked for right at the till
// ---------------------------------------------------------------------------

export function ManagerApproval({
  reason,
  explain,
  permissions,
  onApproved,
  onCancel,
}: {
  reason: string;
  explain: string;
  /** Exactly what is being authorised - nothing wider is granted. */
  permissions: Permission[];
  onApproved: (a: Approval) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const { askApproval } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!username.trim() || !password) return;
    setBusy(true);
    setError('');
    const m = await askApproval(username.trim(), password, permissions, reason);
    setBusy(false);
    if (m) onApproved(m);
    else {
      // Either the sign-in is wrong, or that person cannot authorise this either.
      setError(t('approval.wrongLogin'));
      setPassword('');
    }
  };

  return (
    <Modal
      title={t('approval.title')}
      onClose={onCancel}
      footer={
        <>
          <Button onClick={onCancel}>{t('common.cancel')}</Button>
          <div className="spacer" />
          <Button
            variant="primary"
            onClick={() => void submit()}
            busy={busy}
            disabled={!username.trim() || !password}
          >
            {t('approval.approve')}
          </Button>
        </>
      }
    >
      <p className="small muted">{explain}</p>
      <form
        className="col"
        style={{ marginBlockStart: 'var(--s-4)' }}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Field label={t('login.username')}>
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="off"
            autoCapitalize="none"
            dir="ltr"
            autoFocus
          />
        </Field>
        <Field label={t('login.password')}>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="off"
            dir="ltr"
          />
        </Field>
        {/* Submitting with Enter, without a visible second button. */}
        <button type="submit" hidden />
      </form>
      {error ? (
        <p className="center" style={{ color: 'var(--danger)', marginBlockStart: 'var(--s-3)' }} role="alert">
          {error}
        </p>
      ) : null}
    </Modal>
  );
}

export function useApproval() {
  const { can } = useAuth();
  const [ask, setAsk] = useState<{
    reason: string;
    explain: string;
    permissions: Permission[];
    resolve: (a: Approval | null) => void;
  } | null>(null);

  /**
   * Ask for one action to be authorised.
   *
   * If the person already holds the permission themselves, nobody is asked -
   * which is what makes a custom role like "senior cashier" feel natural rather
   * than bureaucratic.
   */
  const request = (permissions: Permission[], reason: string, explain: string) =>
    new Promise<Approval | null>((resolve) => {
      if (permissions.every(can)) {
        resolve({ managerId: 'self', managerName: '', permissions, approvalToken: '' });
        return;
      }
      setAsk({ reason, explain, permissions, resolve });
    });

  const element = ask ? (
    <ManagerApproval
      reason={ask.reason}
      explain={ask.explain}
      permissions={ask.permissions}
      onApproved={(a) => {
        ask.resolve(a);
        setAsk(null);
      }}
      onCancel={() => {
        ask.resolve(null);
        setAsk(null);
      }}
    />
  ) : null;

  return { request, element };
}

// ---------------------------------------------------------------------------
// Barcode
// ---------------------------------------------------------------------------

export function Barcode({
  value,
  height = 46,
  width = 1.6,
  showText = true,
  fontSize = 13,
}: {
  value: string;
  height?: number;
  width?: number;
  showText?: boolean;
  fontSize?: number;
}) {
  const ref = useRef<SVGSVGElement>(null);
  useEffect(() => {
    if (!ref.current || !value) return;
    try {
      JsBarcode(ref.current, value, {
        format: /^\d{13}$/.test(value) ? 'EAN13' : 'CODE128',
        height,
        width,
        displayValue: showText,
        fontSize,
        margin: 0,
        background: '#ffffff',
        lineColor: '#000000',
        font: 'Inter, sans-serif',
      });
    } catch {
      /* an unprintable code shows as nothing rather than crashing a screen */
    }
  }, [value, height, width, showText, fontSize]);
  return <svg ref={ref} role="img" aria-label={value} />;
}

// ---------------------------------------------------------------------------
// Date range
// ---------------------------------------------------------------------------

export interface DateRange {
  from: string;
  to: string;
  key: RangeKey;
}

/**
 * A date box gives back "2026-09-01". Handing that to `new Date()` reads it as
 * midnight in UTC, which in Bethlehem is 3am - and .toISOString().slice(0, 10)
 * on a local midnight goes the other way and shows the day BEFORE. Both ends
 * are done by hand here so the box shows the day that was picked.
 */
function parseDay(ymd: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

export function useDateRange(initial: RangeKey = 'month') {
  const [range, setRange] = useState<DateRange>(() => ({ ...presetRange(initial), key: initial }));
  return { range, setRange };
}

export function RangePicker({ range, onChange }: { range: DateRange; onChange: (r: DateRange) => void }) {
  const t = useT();
  const presets: { key: RangeKey; label: string }[] = [
    { key: 'today', label: t('common.today') },
    { key: 'yesterday', label: t('common.yesterday') },
    { key: 'week', label: t('common.thisWeek') },
    { key: 'month', label: t('common.thisMonth') },
    { key: 'lastMonth', label: t('common.lastMonth') },
    { key: 'year', label: t('common.thisYear') },
  ];

  return (
    <div className="row wrap no-print" style={{ gap: 'var(--s-2)' }}>
      {presets.map((p) => (
        <Button
          key={p.key}
          size="sm"
          variant={range.key === p.key ? 'primary' : 'default'}
          onClick={() => onChange({ ...presetRange(p.key), key: p.key })}
        >
          {p.label}
        </Button>
      ))}
      <input
        type="date"
        aria-label={t('common.from')}
        value={dayKey(range.from)}
        onChange={(e) => {
          const d = parseDay(e.target.value);
          if (d) onChange({ ...range, from: d.toISOString(), key: 'custom' });
        }}
        style={{ width: 'auto' }}
      />
      <input
        type="date"
        aria-label={t('common.to')}
        value={dayKey(new Date(new Date(range.to).getTime() - 1))}
        onChange={(e) => {
          const d = parseDay(e.target.value);
          if (!d) return;
          d.setDate(d.getDate() + 1);
          onChange({ ...range, to: d.toISOString(), key: 'custom' });
        }}
        style={{ width: 'auto' }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small charts, drawn as plain SVG so they print and need no library
// ---------------------------------------------------------------------------

/**
 * Bars for money over days or hours.
 *
 * It says what it shows: dates or hours along the bottom, and above the bars
 * the busiest one - or the one under a finger or a pointer - with its value.
 * Days and hours with nothing sold are drawn as nothing rather than left out,
 * because a chart that skips the empty days makes a quiet week look busy.
 */
export function BarChart({
  data,
  height = 150,
  format,
  kind,
  from,
  to,
}: {
  data: { label: string; value: number }[];
  height?: number;
  format?: (v: number) => string;
  /** day: labels are YYYY-MM-DD. hour: labels start with the hour number. */
  kind?: 'day' | 'hour';
  /** For days: the whole range asked for, so the empty days at either end show too. */
  from?: string;
  to?: string;
}) {
  const t = useT();
  const { lang } = useI18n();
  const { fmt } = useSettings();
  const [picked, setPicked] = useState<number | null>(null);
  const show = (v: number) => (format ? format(v) : String(v));

  const rows = useMemo(() => fillGaps(data, kind, from, to), [data, kind, from, to]);
  if (rows.length === 0) return null;
  const max = Math.max(1, ...rows.map((d) => Math.abs(d.value)));
  const best = rows.reduce((b, d, i) => (Math.abs(d.value) > Math.abs(rows[b].value) ? i : b), 0);
  const every = Math.max(1, Math.ceil(rows.length / (kind === 'hour' ? 8 : 10)));

  const locale = lang === 'ar' ? 'ar-PS-u-nu-latn' : 'en-GB';
  const dayOf = (key: string) => {
    const [y, m, d] = key.split('-').map(Number);
    return new Date(y, (m ?? 1) - 1, d ?? 1);
  };
  const long = (label: string) => {
    if (kind === 'day' && /^\d{4}-\d{2}-\d{2}$/.test(label)) {
      return dayOf(label).toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' });
    }
    if (kind === 'hour') return `${String(parseInt(label, 10)).padStart(2, '0')}:00`;
    return label;
  };
  const short = (label: string, i: number) => {
    if (kind === 'day' && /^\d{4}-\d{2}-\d{2}$/.test(label)) {
      const d = dayOf(label);
      const newMonth = i === 0 || d.getDate() === 1;
      return newMonth ? d.toLocaleDateString(locale, { day: 'numeric', month: 'short' }) : String(d.getDate());
    }
    if (kind === 'hour') return String(parseInt(label, 10));
    return label;
  };
  const digits = (s: string) => (fmt.arabicDigits ? s.replace(/[0-9]/g, (c) => '٠١٢٣٤٥٦٧٨٩'[Number(c)]) : s);
  const shownIndex = picked ?? best;
  const shownRow = rows[shownIndex];

  return (
    <div className="bars" onPointerLeave={() => setPicked(null)}>
      <div className="bars-readout" aria-live="polite">
        <span className="muted">
          {picked === null ? (kind === 'hour' ? t('chart.busiestHour') : kind === 'day' ? t('chart.bestDay') : t('chart.highest')) : ''}
        </span>{' '}
        <span className="strong">{digits(long(shownRow.label))}</span>
        <span className="tnum strong">{show(shownRow.value)}</span>
      </div>
      <div className="bars-plot" style={{ height }} role="img" aria-label={`${t('chart.bestDay')} ${long(rows[best].label)} ${show(rows[best].value)}`}>
        {rows.map((d, i) => (
          <span
            key={d.label}
            className={`bars-col ${i === shownIndex ? 'on' : ''}`}
            onPointerEnter={() => setPicked(i)}
            onClick={() => setPicked(i)}
          >
            <span className="bars-bar" style={{ height: `${d.value ? Math.max(2, (Math.abs(d.value) / max) * 100) : 0}%` }} />
          </span>
        ))}
      </div>
      <div className="bars-axis" aria-hidden>
        {rows.map((d, i) => (
          <span key={d.label}>{i % every === 0 ? digits(short(d.label, i)) : ''}</span>
        ))}
      </div>
    </div>
  );
}

/** Every day in the range (up to four months) and every shop hour, whether anything sold or not. */
function fillGaps(
  data: { label: string; value: number }[],
  kind?: 'day' | 'hour',
  from?: string,
  to?: string,
): { label: string; value: number }[] {
  if (kind === 'hour') {
    const byHour = new Map(data.map((d) => [parseInt(d.label, 10), d.value]));
    const hours = [...byHour.keys()].filter((h) => !Number.isNaN(h));
    if (!hours.length) return data;
    const first = Math.min(9, ...hours);
    const last = Math.max(20, ...hours);
    return Array.from({ length: last - first + 1 }, (_, i) => ({ label: String(first + i), value: byHour.get(first + i) ?? 0 }));
  }
  if (kind === 'day') {
    const known = data.filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d.label));
    if (known.length !== data.length) return data;
    const start = from ? dayKey(new Date(from)) : known[0]?.label;
    const end = to ? dayKey(new Date(new Date(to).getTime() - 1)) : known[known.length - 1]?.label;
    if (!start || !end) return data;
    const byDay = new Map(known.map((d) => [d.label, d.value]));
    const out: { label: string; value: number }[] = [];
    const [y, m, d] = start.split('-').map(Number);
    const cur = new Date(y, m - 1, d);
    // Stop at today: the rest of this month has not happened yet.
    const today = dayKey(new Date());
    for (let i = 0; i < 124; i++) {
      const key = dayKey(cur);
      if (key > end || key > today) break;
      out.push({ label: key, value: byDay.get(key) ?? 0 });
      cur.setDate(cur.getDate() + 1);
    }
    return out.length ? out : data;
  }
  return data;
}

export function BarList({
  rows,
  format,
  max: maxOverride,
}: {
  rows: { label: string; value: number; sub?: string }[];
  format: (v: number) => string;
  max?: number;
}) {
  const max = maxOverride ?? Math.max(1, ...rows.map((r) => Math.abs(r.value)));
  return (
    <div className="col" style={{ gap: 'var(--s-3)' }}>
      {rows.map((r, i) => (
        <div key={i}>
          <div className="row" style={{ gap: 'var(--s-3)', marginBlockEnd: 4 }}>
            <div className="small strong" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.label}
            </div>
            {r.sub ? <div className="xsmall muted">{r.sub}</div> : null}
            <div className="small strong tnum">{format(r.value)}</div>
          </div>
          <div style={{ height: 6, background: 'var(--surface-3)', borderRadius: 3, overflow: 'hidden' }}>
            <div
              style={{
                height: '100%',
                width: `${(Math.abs(r.value) / max) * 100}%`,
                background: 'var(--accent)',
                borderRadius: 3,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

export function Kpi({
  label,
  value,
  detail,
  trend,
  accent,
}: {
  label: string;
  value: string;
  detail?: string;
  trend?: 'up' | 'down';
  accent?: boolean;
}) {
  return (
    <div className={`kpi ${accent ? 'accent' : ''}`}>
      <div className="k">{label}</div>
      <div className="v">{value}</div>
      {detail ? <div className={`d ${trend ?? ''}`}>{detail}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Camera scanning, for phones and tablets
// ---------------------------------------------------------------------------

export function CameraScan({ onScan, onClose }: { onScan: (code: string) => void; onClose: () => void }) {
  const t = useT();
  const video = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState('');
  const lastCode = useRef<{ code: string; at: number }>({ code: '', at: 0 });

  useEffect(() => {
    let scanner: CameraScanner | null = null;
    let alive = true;

    void (async () => {
      try {
        if (!video.current) return;
        scanner = await startCameraScan(video.current, (code) => {
          // The camera reads the same barcode many times a second; only act once.
          const now = Date.now();
          if (lastCode.current.code === code && now - lastCode.current.at < 1800) return;
          lastCode.current = { code, at: now };
          beepOk();
          onScan(code);
        });
        if (!alive) scanner.stop();
      } catch (e) {
        setError((e as Error).message);
      }
    })();

    return () => {
      alive = false;
      scanner?.stop();
    };
  }, [onScan]);

  return (
    <Modal title={t('exchange.scanReceipt')} onClose={onClose} footer={<Button onClick={onClose}>{t('common.close')}</Button>}>
      {error ? (
        <p style={{ color: 'var(--danger)' }}>{error}</p>
      ) : (
        <div className="scanner-view">
          <video ref={video} muted playsInline />
          <div className="scanner-frame" />
        </div>
      )}
    </Modal>
  );
}

export function CameraButton({ onScan, label }: { onScan: (code: string) => void; label?: string }) {
  const [open, setOpen] = useState(false);
  if (!cameraAvailable()) return null;
  return (
    <>
      <Button onClick={() => setOpen(true)} aria-label={label ?? 'Scan with the camera'}>
        📷
      </Button>
      {open ? (
        <CameraScan
          onScan={(c) => {
            setOpen(false);
            onScan(c);
          }}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Money helper used in dozens of places
// ---------------------------------------------------------------------------

export function Money({ value, code, className }: { value: number; code?: string; className?: string }) {
  const { money } = useSettings();
  return <span className={`tnum ${className ?? ''}`}>{money(value, code)}</span>;
}

export function useUnknownBarcodeSound() {
  return useMemo(() => beepUnknown, []);
}
